import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { IvrAction } from '@app/core/interfaces/ivr-action';
import { IvrAssistant } from '@app/core/interfaces/ivr-assistant';
import { IvrBlock } from '@app/core/interfaces/ivr-block';
import { IvrPrompt } from '@app/core/interfaces/ivr-prompt';
import { NewPhoneNumber } from '@app/core/interfaces/new-phone-number';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { Identifier } from '@app/utils/common';
import { wrapError } from '@app/utils/rxjs';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class IvrRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  addAction(action: Partial<IvrAction>, blockId: Identifier, ivrId: Identifier): Observable<IvrAction> {
    return this.utils.apiUrl(`/ivr/${ivrId}/blocks/${blockId}/actions`).pipe(
      mergeMap((url) => this.http.post<IvrAction>(url, action)),
      wrapError('ivr-repository:add-action')
    );
  }

  addBlock(block: Partial<IvrBlock>, ivrId: number): Observable<void> {
    return this.utils.apiUrl(`/ivr/${ivrId}/blocks`).pipe(
      mergeMap((url) => this.http.post<void>(url, block)),
      wrapError('ivr-repository:add-block')
    );
  }

  addIvr(ivr: Partial<IvrAssistant>): Observable<void> {
    return this.utils.apiUrl(`/ivr`).pipe(
      mergeMap((url) => this.http.post<void>(url, ivr)),
      wrapError('ivr-repository:add-ivr')
    );
  }

  addPhoneNumber(ivrId: Identifier, phoneNumber: NewPhoneNumber): Observable<PhoneNumber[]> {
    return this.utils.apiUrl(`/ivr/${ivrId}/numbers`).pipe(
      mergeMap((url) => this.http.post<PhoneNumber[]>(url, phoneNumber)),
      wrapError('ivr-repository:add-phone-number')
    );
  }

  addPrompt(assistantId: Identifier, prompt: Partial<IvrPrompt>): Observable<IvrPrompt> {
    return this.utils.apiUrl(`/ivr/${assistantId}/blocks/${prompt.blockId}/prompts`).pipe(
      mergeMap((url) => this.http.post<IvrPrompt>(url, prompt)),
      wrapError('ivr-repository:add-prompt')
    );
  }

  deleteAction(actionId: Identifier, blockId: Identifier, ivrId: Identifier): Observable<void> {
    return this.utils.apiUrl(`/ivr/${ivrId}/blocks/${blockId}/actions/${actionId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('ivr-repository:delete-action')
    );
  }

  deleteBlock(id: Identifier, ivrId: Identifier): Observable<void> {
    return this.utils.apiUrl(`/ivr/${ivrId}/blocks/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('ivr-repository:delete-block')
    );
  }

  deleteIvr(id: Identifier): Observable<void> {
    return this.utils.apiUrl(`/ivr/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('ivr-repository:delete-ivr')
    );
  }

  deletePhoneNumber(ivrId: Identifier, phoneNumberId: number): Observable<PhoneNumber[]> {
    return this.utils.apiUrl(`/ivr/${ivrId}/numbers/${phoneNumberId}`).pipe(
      mergeMap((url) => this.http.delete<PhoneNumber[]>(url)),
      wrapError('ivr-repository:delete-phone-number')
    );
  }

  deletePrompt(assistantId: Identifier, blockId: Identifier, promptId: Identifier): Observable<void> {
    return this.utils.apiUrl(`/ivr/${assistantId}/blocks/${blockId}/prompts/${promptId}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('ivr-repository:delete-prompt')
    );
  }

  getBlock(ivrId: number, blockId: number): Observable<IvrBlock> {
    return this.utils.apiUrl(`/ivr/${ivrId}/blocks/${blockId}`).pipe(
      mergeMap((url) => this.http.get<IvrBlock>(url)),
      wrapError('ivr-repository:get-block')
    );
  }

  get(id: Identifier): Observable<IvrAssistant> {
    return this.utils.apiUrl(`/ivr/${id}`).pipe(
      mergeMap((url) => this.http.get<IvrAssistant>(url)),
      wrapError('ivr-repository:get-ivr')
    );
  }

  list(enterpriseId: number | string): Observable<IvrAssistant[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin]: BaseUrls.ServiceProvider.Enterprise + '/ivr',
      [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + '/ivr',
    };

    return this.utils.apiUrl(urls, { enterpriseId }).pipe(
      mergeMap((url) => this.http.get<IvrAssistant[]>(url)),
      wrapError('ivr-repository:list')
    );
  }

  listPrompts(assistantId: Identifier, blockId: Identifier): Observable<IvrPrompt[]> {
    return this.utils.apiUrl(`/ivr/${assistantId}/blocks/${blockId}/prompts`).pipe(
      mergeMap((url) => this.http.get<IvrPrompt[]>(url)),
      wrapError('ivr-repository:list-prompts')
    );
  }

  updateAction(action: IvrAction, blockId: Identifier, ivrId: Identifier): Observable<void> {
    return this.utils.apiUrl(`/ivr/${ivrId}/blocks/${blockId}/actions/${action.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, action)),
      wrapError('ivr-repository:update-action')
    );
  }

  updateBlock(block: IvrBlock, ivrId: number): Observable<void> {
    return this.utils.apiUrl(`/ivr/${ivrId}/blocks/${block.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, block)),
      wrapError('ivr-repository:update-block')
    );
  }

  updateIvr(ivr: IvrAssistant): Observable<void> {
    return this.utils.apiUrl(`/ivr/${ivr.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, ivr)),
      wrapError('ivr-repository:update-ivr')
    );
  }

  updatePrompt(assistantId: Identifier, prompt: IvrPrompt): Observable<void> {
    return this.utils.apiUrl(`/ivr/${assistantId}/blocks/${prompt.blockId}/prompts/${prompt.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, prompt)),
      wrapError('ivr-repository:update-prompt')
    );
  }
}
